import React, {useRef, useState} from "react";
import {Box, Button} from "@material-ui/core";
import ReactCrop, {Crop, PixelCrop} from "react-image-crop";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import FlexBox from "view/components/Layout/FlexBox";
import Alert from "view/components/Alert";
import {Image} from "view/components/RichTextEditor/types";
import useStyles from "../../styles";
import {getCroppedImage} from "./getCroppedImage";

import "react-image-crop/dist/ReactCrop.css";

type CropImageProps = {
  imageSource: string;
  extension: Image["extension"];
  onSave: (src: string) => void;
  onCancel: () => void;
};

const CropImage = ({
  imageSource,
  onSave,
  onCancel,
  extension,
}: CropImageProps) => {
  const classes = useStyles();

  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const [crop, setCrop] = useState<Crop>({
    unit: "px",
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [error, setError] = useState("");

  return (
    <Box className={classes.container}>
      <FlexBox
        justifyContent="space-between"
        alignCenter
        className={classes.actionButtons}
      >
        <Box mr={2}>
          <Button
            color="primary"
            variant="contained"
            className={classes.iconButton}
            onClick={() => {
              const canvas = previewCanvasRef.current;
              const image = imgRef.current;
              if (!canvas || !image || !completedCrop) {
                return;
              }
              try {
                onSave(
                  getCroppedImage(image, canvas, completedCrop, extension)
                );
              } catch (e) {
                // Last-resort guard. `EditImageModal` inlines a remote image
                // into a data URI before opening this step precisely so the
                // canvas can't be tainted, so reaching here means something
                // else went wrong — surface it rather than leaving the
                // confirm button looking dead.
                setError("Couldn't apply the crop. Please try again.");
                // eslint-disable-next-line no-console
                console.error("RichTextEditor: failed to crop the image", e);
              }
            }}
          >
            <CheckIcon />
          </Button>
        </Box>
        <Button
          color="primary"
          variant="outlined"
          className={classes.iconButton}
          onClick={onCancel}
        >
          <CloseIcon />
        </Button>
      </FlexBox>
      {error && (
        <Box mb={2}>
          <Alert severity="error" fullWidth>
            {error}
          </Alert>
        </Box>
      )}
      <ReactCrop
        crop={crop}
        onChange={(c) => setCrop(c)}
        onComplete={(c) => setCompletedCrop(c)}
      >
        <img ref={imgRef} src={imageSource} alt="" />
      </ReactCrop>
      <canvas
        ref={previewCanvasRef}
        style={{
          display: "none",
          width: completedCrop?.width,
          height: completedCrop?.height,
        }}
      />
    </Box>
  );
};

export default CropImage;
